import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { UserRole } from '@nbh/shared';
import { useAuth } from '../../auth/AuthProvider';
import type { SessionUser } from '../../lib/api';
import { SuperAdminAudit } from './SuperAdminAudit';
import { SuperAdminJobManager } from './SuperAdminJobManager';
import { SuperAdminRooms } from './SuperAdminRooms';
import { SuperAdminStaff } from './SuperAdminStaff';

type Section = 'staff' | 'rooms' | 'jobs' | 'audit';

// Audit last: it is the record of what the first two sections did, so it reads in the
// order the work happens.
const SECTIONS: Section[] = ['staff', 'rooms', 'jobs', 'audit'];

/**
 * The signed-in user, or null when there is no session in context.
 *
 * `useAuth` throws outside an `AuthProvider` — correct for the app, wrong here: this
 * portal is mounted inside a surface that is also rendered standalone. Missing context
 * is read as "no session", so the failure mode is a hidden portal, never an open one.
 */
function useSessionUser(): SessionUser | null {
  try {
    return useAuth().user;
  } catch {
    return null;
  }
}

/**
 * Root-tier controls (decision D23).
 *
 * The gate runs before the body mounts, so an ordinary admin issues no root-tier
 * request at all. That is a courtesy, not the control: `/api/super-admin/*` is guarded
 * server-side, and hiding a button has never stopped anyone from calling an endpoint.
 * What it does buy is an operator never being shown an action that would 403 at them.
 */
export function SuperAdminPortal() {
  const user = useSessionUser();
  if (user?.role !== UserRole.SuperAdmin) return null;

  return <SuperAdminPortalBody selfId={user.id} />;
}

function SuperAdminPortalBody({ selfId }: { selfId: string }) {
  const { t } = useTranslation();
  const [section, setSection] = useState<Section>('staff');

  return (
    <section className="rounded-xl border-2 border-amber-700/50 bg-amber-950/10">
      <header className="px-3 pt-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-200">
          <ShieldAlert className="size-4 shrink-0" aria-hidden="true" />
          {t('superAdmin.heading')}
        </h2>
        <p className="mt-1 text-xs text-slate-400">{t('superAdmin.explain')}</p>
      </header>

      <nav
        className="mt-3 flex overflow-x-auto border-b border-slate-800"
        aria-label={t('superAdmin.tabs.label')}
      >
        {SECTIONS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={section === name}
            onClick={() => setSection(name)}
            className={`min-h-[48px] shrink-0 border-b-2 bg-transparent px-4 text-sm font-medium ${
              section === name
                ? 'border-amber-400 text-slate-100'
                : 'border-transparent text-slate-400'
            }`}
          >
            {t(`superAdmin.tabs.${name}`)}
          </button>
        ))}
      </nav>

      <div className="p-3">
        {section === 'staff' && <SuperAdminStaff selfId={selfId} />}
        {section === 'rooms' && <SuperAdminRooms />}
        {section === 'jobs' && <SuperAdminJobManager />}
        {section === 'audit' && <SuperAdminAudit />}
      </div>
    </section>
  );
}
